import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiArrowLeft,
  FiCopy,
  FiCpu,
  FiDownload,
  FiLayers,
  FiLock,
  FiRefreshCw,
  FiUpload
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { getStoredSession, subscribeToSessionChanges } from '../../api/authStorage';
import { usePersistedOfflineSignerAddress } from '../../hooks/usePersistedOfflineSignerAddress';
import {
  CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID,
  DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID,
  listStandardContractTemplates,
  resolveContractArtifact
} from '../../blockchain/standardContractTemplates';
import { resolveContractTemplateIdForAssetClass } from '../../blockchain/resolveMintArtifactForAssetClass';
import { normalizeAssetClassTokenStandard } from '../../api/smartTokenizerMappers';
import {
  MINTED_ASSET_STATUSES,
  TOKENIZABLE_ASSET_STATUSES,
  buildSignerOutputTemplate,
  copyTextToClipboard,
  delay,
  downloadJsonFile,
  formatPreparedPayload,
  parseSignedTransactionInput
} from '../../blockchain/offlineSigning';
import TransactionLookupDrawer from '../BlockchainExplorer/TransactionLookupDrawer';

const METADATA_READY_STATUSES = new Set(['MetadataPinnedToIpfs', 'metadata_pinned_to_ipfs']);

function normalizeBulkMintPrepareResponse(data) {
  const rows = data?.items ?? data?.Items ?? [];
  return {
    bulkRegistrationId: data?.bulkRegistrationId ?? data?.BulkRegistrationId,
    items: rows.map((row) => ({
      assetId: row.assetId ?? row.AssetId,
      prepared: row.prepared ?? row.Prepared
    }))
  };
}

function bulkRegistrationMembers(b) {
  return b?.assets ?? b?.Assets ?? [];
}

function isMintReadyApiAsset(a) {
  if (!a) return false;
  const tid = a.tokenizationId ?? a.TokenizationId;
  if (tid) return false;
  const s = a.status ?? a.Status ?? '';
  return TOKENIZABLE_ASSET_STATUSES.has(s) || METADATA_READY_STATUSES.has(s);
}

const isMintReadyAsset = (asset) =>
  !asset?.tokenizationId &&
  (TOKENIZABLE_ASSET_STATUSES.has(asset.status) || METADATA_READY_STATUSES.has(asset.status));

const hasDeployedAddress = (ac) => Boolean(ac?.contractAddress && String(ac.contractAddress).trim());

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
`;

const BackRow = styled.div`
  margin-bottom: 1.5rem;

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #9bd6ff;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: #c4e4ff;
    }
  }
`;

const TitleBlock = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #a0a0a0;
    line-height: 1.55;
    max-width: 720px;
  }
`;

const GatePanel = styled.div`
  max-width: 520px;
  margin: 3rem auto;
  padding: 2rem;
  background: rgba(255, 100, 100, 0.08);
  border: 1px solid rgba(255, 100, 100, 0.25);
  border-radius: 16px;
  color: #fecaca;
  text-align: center;
  line-height: 1.6;

  svg {
    display: block;
    margin: 0 auto 1rem;
    font-size: 2.5rem;
    color: #f87171;
  }

  h2 {
    color: #fff;
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
    font-weight: 700;
  }
`;

const Section = styled.section`
  margin-bottom: 2.5rem;

  h2 {
    color: #fff;
    font-size: 1.25rem;
    margin-bottom: 1rem;
    font-weight: 600;
  }
`;

const ContractGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const ContractCard = styled.div`
  text-align: left;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid ${(p) => (p.$active ? '#4facfe' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 12px;
  padding: 1.25rem;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #4facfe;
  }

  .name {
    font-weight: 700;
    color: #fff;
    margin-bottom: 0.35rem;
  }

  .code {
    font-size: 0.85rem;
    color: #4facfe;
    margin-bottom: 0.5rem;
  }

  .addr {
    font-family: Consolas, Monaco, monospace;
    font-size: 0.75rem;
    color: #a0a0a0;
    word-break: break-all;
    margin-bottom: 0.5rem;
  }

  .meta {
    font-size: 0.8rem;
    color: #888;
  }

  .warn {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: #fbbf24;
  }
`;

const FormPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    color: #a0a0a0;
    font-size: 0.85rem;
    margin-bottom: 0.35rem;
  }

  input,
  select,
  textarea {
    width: 100%;
    max-width: 480px;
    padding: 0.65rem 0.85rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    font-size: 0.95rem;
  }

  textarea {
    font-family: Consolas, Monaco, monospace;
    font-size: 0.82rem;
    min-height: 120px;
    resize: vertical;
  }
`;

const FlowSteps = styled.ol`
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  color: #a0a0a0;
  line-height: 1.55;
  font-size: 0.9rem;

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }
`;

const StatusPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: #fff;

  .title {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #4facfe;
  }

  .message {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1rem;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const PayloadBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  .title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .hint {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }

  textarea {
    width: 100%;
    min-height: 160px;
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 1rem;
    resize: vertical;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.85rem;
  }
`;

const PayloadActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MiniButton = styled.button.attrs({ type: 'button' })`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const FilePickLabel = styled.label`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  input {
    display: none;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const PrimaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  background: #4facfe;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #3d8bfe;
  }

  &:disabled {
    background: #2a2a3e;
    color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(79, 172, 254, 0.12);
  color: #4facfe;
  border: 1px solid currentColor;
`;

const HashButton = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: none;
  padding: 0;
  color: #4facfe;
  font-family: Consolas, Monaco, monospace;
  cursor: pointer;
  text-decoration: underline;
`;

function OfflineTokenMintPage() {
  const [hasSession, setHasSession] = useState(() => Boolean(getStoredSession()?.accessToken));
  const { isAdmin, isAuthorizedTokenizer, currentIssuer, session } = useTokenIssuer();
  const offlineSignerUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id || null;
  const [signerAddress, setSignerAddress] = usePersistedOfflineSignerAddress(offlineSignerUserId);
  const { assetClasses, loadAssetClasses } = useAssetClass();
  const {
    assets,
    loading,
    getAssetById,
    prepareMintToken,
    prepareBulkMintByRegistration,
    submitMintToken,
    getMintedTokenByAssetId,
    listAssetBulkRegistrations,
    loadAssets
  } = useTokenization();

  const [selectedClassId, setSelectedClassId] = useState('');
  const [mintMode, setMintMode] = useState('single');
  const [bulkRegistrations, setBulkRegistrations] = useState([]);
  const [bulkRegsLoading, setBulkRegsLoading] = useState(false);
  const [selectedBulkId, setSelectedBulkId] = useState('');
  const [bulkMintLineItems, setBulkMintLineItems] = useState([]);
  const [bulkMintCursor, setBulkMintCursor] = useState(0);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [ownerWallet, setOwnerWallet] = useState('');
  const [preparedMint, setPreparedMint] = useState(null);
  const [signedMintInput, setSignedMintInput] = useState('');
  const [mintStatus, setMintStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inspectedTransactionHash, setInspectedTransactionHash] = useState(null);
  const [contractTemplateId, setContractTemplateId] = useState(DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID);
  const [customContractAbiJson, setCustomContractAbiJson] = useState('');
  const [customMintFunctionName, setCustomMintFunctionName] = useState('mint');
  const [mintAmount, setMintAmount] = useState('1000000000000000000');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('mint') !== 'bulk') {
      return;
    }
    setMintMode('bulk');
    const next = new URLSearchParams(searchParams);
    next.delete('mint');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setHasSession(Boolean(getStoredSession()?.accessToken));
    return subscribeToSessionChanges((s) => {
      setHasSession(Boolean(s?.accessToken));
    });
  }, []);

  useEffect(() => {
    loadAssetClasses();
  }, [loadAssetClasses]);

  const currentUserId = offlineSignerUserId;

  const deployedForUser = useMemo(() => {
    const deployed = assetClasses.filter(hasDeployedAddress);
    if (isAdmin) {
      return deployed;
    }
    const myClassIds = new Set(assets.map((a) => a.assetClassId).filter(Boolean));
    return deployed.filter((ac) => myClassIds.has(ac.id));
  }, [assetClasses, assets, isAdmin]);

  useEffect(() => {
    if (deployedForUser.length === 1 && !selectedClassId) {
      setSelectedClassId(deployedForUser[0].id);
    }
  }, [deployedForUser, selectedClassId]);

  const selectedClass = useMemo(
    () => deployedForUser.find((c) => c.id === selectedClassId) || null,
    [deployedForUser, selectedClassId]
  );

  const isClassErc721 = normalizeAssetClassTokenStandard(selectedClass?.tokenStandard) === 'ERC721';

  const bulkRegsForClass = useMemo(() => {
    if (!selectedClassId) return [];
    return bulkRegistrations.filter((b) => {
      const cid = b.assetClassId ?? b.AssetClassId;
      return String(cid) === String(selectedClassId);
    });
  }, [bulkRegistrations, selectedClassId]);

  const eligibleBulkRegs = useMemo(() => {
    return bulkRegsForClass.filter((b) => {
      const members = bulkRegistrationMembers(b);
      return members.length > 0 && members.every(isMintReadyApiAsset);
    });
  }, [bulkRegsForClass]);

  useEffect(() => {
    if (!isClassErc721 && mintMode === 'bulk') {
      setMintMode('single');
    }
  }, [isClassErc721, mintMode]);

  useEffect(() => {
    if (mintMode !== 'bulk' || !selectedClassId || !isClassErc721) {
      setBulkRegistrations([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setBulkRegsLoading(true);
      try {
        const raw = await listAssetBulkRegistrations();
        if (!cancelled) {
          setBulkRegistrations(Array.isArray(raw) ? raw : []);
        }
      } catch {
        if (!cancelled) {
          setBulkRegistrations([]);
          toast.error('Could not load bulk registrations');
        }
      } finally {
        if (!cancelled) setBulkRegsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintMode, selectedClassId, isClassErc721, listAssetBulkRegistrations]);

  useEffect(() => {
    setSelectedBulkId('');
    setBulkMintLineItems([]);
    setBulkMintCursor(0);
  }, [selectedClassId]);

  useEffect(() => {
    if (mintMode === 'bulk') {
      setSelectedAssetId('');
    } else {
      setSelectedBulkId('');
      setBulkMintLineItems([]);
      setBulkMintCursor(0);
    }
    setPreparedMint(null);
    setSignedMintInput('');
    setMintStatus(null);
  }, [mintMode]);

  const mintableForClass = useMemo(() => {
    if (!selectedClassId) {
      return [];
    }
    return assets.filter(
      (a) => a.assetClassId === selectedClassId && isMintReadyAsset(a)
    );
  }, [assets, selectedClassId]);

  const selectedAsset = useMemo(
    () => assets.find((a) => a.id === selectedAssetId) || null,
    [assets, selectedAssetId]
  );

  const classTokenStandard = normalizeAssetClassTokenStandard(selectedClass?.tokenStandard);
  const isErc1155 = classTokenStandard === 'ERC1155';
  const isErc20 = classTokenStandard === 'ERC20';

  const resolvedMintArtifactPreview = useMemo(() => {
    try {
      return resolveContractArtifact(contractTemplateId, {
        abiJson: customContractAbiJson,
        bytecode: '',
        mintFunctionName: customMintFunctionName
      });
    } catch {
      return null;
    }
  }, [contractTemplateId, customContractAbiJson, customMintFunctionName]);

  const signerOutputTemplate = formatPreparedPayload(
    buildSignerOutputTemplate({ expectedContractAddress: null })
  );

  useEffect(() => {
    setContractTemplateId((prev) => {
      if (prev === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
        return prev;
      }
      if (!selectedClass) {
        return DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID;
      }
      return resolveContractTemplateIdForAssetClass(selectedClass);
    });
    setCustomContractAbiJson('');
    setCustomMintFunctionName('mint');
    setMintAmount(normalizeAssetClassTokenStandard(selectedClass?.tokenStandard) === 'ERC1155' ? '1' : '1000000000000000000');
  }, [selectedClass?.id, selectedClass?.category, selectedClass?.tokenStandard]);

  useEffect(() => {
    setSelectedAssetId('');
    setPreparedMint(null);
    setSignedMintInput('');
    setMintStatus(null);
  }, [selectedClassId]);

  useEffect(() => {
    if (!selectedAssetId) {
      return;
    }
    const a = assets.find((x) => x.id === selectedAssetId);
    if (a) {
      setOwnerWallet(a.ownerAddress || '');
    }
    setPreparedMint(null);
    setSignedMintInput('');
    setMintStatus(null);
  }, [selectedAssetId, assets]);

  useEffect(() => {
    if (mintMode !== 'bulk' || !selectedBulkId) {
      return;
    }
    const b = bulkRegsForClass.find((x) => String(x.id ?? x.Id) === String(selectedBulkId));
    if (!b) {
      return;
    }
    const members = bulkRegistrationMembers(b);
    const first = members[0];
    const addr = first?.ownerAddress ?? first?.OwnerAddress ?? '';
    if (addr) {
      setOwnerWallet(String(addr).trim());
    }
    setPreparedMint(null);
    setSignedMintInput('');
    setMintStatus(null);
    setBulkMintLineItems([]);
    setBulkMintCursor(0);
  }, [mintMode, selectedBulkId, bulkRegsForClass]);

  useEffect(() => {
    if (!selectedAssetId) {
      return;
    }
    if (!mintableForClass.some((a) => a.id === selectedAssetId)) {
      setSelectedAssetId('');
    }
  }, [mintableForClass, selectedAssetId]);

  const refreshBulkRegistrations = useCallback(async () => {
    if (mintMode !== 'bulk' || !isClassErc721) return;
    setBulkRegsLoading(true);
    try {
      const raw = await listAssetBulkRegistrations();
      setBulkRegistrations(Array.isArray(raw) ? raw : []);
      toast.success('Bulk registrations refreshed');
    } catch {
      setBulkRegistrations([]);
      toast.error('Could not load bulk registrations');
    } finally {
      setBulkRegsLoading(false);
    }
  }, [mintMode, isClassErc721, listAssetBulkRegistrations]);

  const handleCopyPayload = async (payload) => {
    await copyTextToClipboard(formatPreparedPayload(payload));
    toast.success('Copied JSON payload');
  };

  const handleCopyTemplate = async () => {
    await copyTextToClipboard(signerOutputTemplate);
    toast.success('Copied signer template');
  };

  const handleDownloadPayload = (filename, payload) => {
    downloadJsonFile(filename, payload);
    toast.success(`Downloaded ${filename}`);
  };

  const handleSignedFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text) {
        toast.error('File was empty');
        return;
      }
      setSignedMintInput(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  const pollForMintCompletion = useCallback(
    async (assetId) => {
      const std = normalizeAssetClassTokenStandard(selectedClass?.tokenStandard);
      const tokenType =
        std === 'ERC1155' ? 'fractionalNFT' : std === 'ERC20' ? 'fungible' : 'nonFungible';
      const asset = assets.find((x) => x.id === assetId);
      const name = `${asset?.name || 'Asset'} Token`;
      const symbol = asset?.assetNumber?.slice(0, 6)?.toUpperCase() || 'ASSET';
      const cfg = { tokenType, name, symbol };

      for (let attempt = 0; attempt < 12; attempt += 1) {
        const mintedToken = await getMintedTokenByAssetId(assetId, cfg);
        if (mintedToken) {
          return mintedToken;
        }
        const refreshedAsset = await getAssetById(assetId, true);
        if (MINTED_ASSET_STATUSES.has(refreshedAsset?.status)) {
          return getMintedTokenByAssetId(assetId, cfg);
        }
        await delay(5000);
      }
      return null;
    },
    [assets, getAssetById, getMintedTokenByAssetId, selectedClass?.tokenStandard]
  );

  const handlePrepareMint = async () => {
    if (!currentUserId) {
      toast.error('Please sign in before preparing transactions.');
      return;
    }
    if (!selectedClass?.contractAddress) {
      toast.error('Select a deployed contract.');
      return;
    }

    if (mintMode === 'bulk') {
      if (!isClassErc721) {
        toast.error('Bulk mint by registration supports ERC-721 asset classes only.');
        return;
      }
      if (!selectedBulkId) {
        toast.error('Select a bulk registration whose members are all mint-ready.');
        return;
      }
      if (!signerAddress?.trim()) {
        toast.error('Offline signer address is required.');
        return;
      }
      if (!ownerWallet?.trim()) {
        toast.error('Recipient wallet is required.');
        return;
      }
      if (contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
        try {
          resolveContractArtifact(CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID, {
            abiJson: customContractAbiJson,
            bytecode: '',
            mintFunctionName: customMintFunctionName
          });
        } catch (e) {
          toast.error(e.message || 'Invalid custom contract ABI');
          return;
        }
        if (!String(customMintFunctionName || '').trim()) {
          toast.error('Mint function name is required.');
          return;
        }
      }
      try {
        setIsSubmitting(true);
        const artifact = resolveContractArtifact(contractTemplateId, {
          abiJson: customContractAbiJson,
          bytecode: '',
          mintFunctionName: customMintFunctionName
        });
        const raw = await prepareBulkMintByRegistration(selectedBulkId, {
          signerAddress: signerAddress.trim(),
          ownerWallet: ownerWallet.trim(),
          contractAddress: selectedClass.contractAddress,
          contractAbi: artifact.abiJson,
          functionName: artifact.mintFunctionName,
          tokenStandard: 'ERC721'
        });
        const normalized = normalizeBulkMintPrepareResponse(raw);
        if (!normalized.items.length) {
          toast.error('Bulk prepare returned no line items.');
          return;
        }
        setBulkMintLineItems(normalized.items);
        setBulkMintCursor(0);
        setPreparedMint(normalized.items[0].prepared);
        toast.success(
          `Prepared ${normalized.items.length} unsigned mints — sign each JSON in order, then broadcast in order.`
        );
      } catch (error) {
        const msg =
          error.response?.data?.message || error.response?.data?.Message || error.message || 'Failed to prepare bulk mint';
        toast.error(msg);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!selectedAsset) {
      toast.error('Select a mint-ready asset.');
      return;
    }
    if (!signerAddress?.trim()) {
      toast.error('Offline signer address is required.');
      return;
    }
    if (!ownerWallet?.trim()) {
      toast.error('Recipient wallet is required.');
      return;
    }
    if (contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
      try {
        resolveContractArtifact(CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID, {
          abiJson: customContractAbiJson,
          bytecode: '',
          mintFunctionName: customMintFunctionName
        });
      } catch (e) {
        toast.error(e.message || 'Invalid custom contract ABI');
        return;
      }
      if (!String(customMintFunctionName || '').trim()) {
        toast.error('Mint function name is required.');
        return;
      }
    }

    const amtPrepare = String(mintAmount || '').trim();
    if ((isErc20 || isErc1155) && !amtPrepare) {
      toast.error('Enter a mint amount.');
      return;
    }

    try {
      setIsSubmitting(true);
      const artifact = resolveContractArtifact(contractTemplateId, {
        abiJson: customContractAbiJson,
        bytecode: '',
        mintFunctionName: customMintFunctionName
      });
      const amt = amtPrepare;
      const basePrepare = {
        signerAddress: signerAddress.trim(),
        ownerWallet: ownerWallet.trim(),
        contractAddress: selectedClass.contractAddress,
        contractAbi: artifact.abiJson,
        functionName: artifact.mintFunctionName
      };
      let preparedMintPayload;
      if (isErc20) {
        preparedMintPayload = await prepareMintToken(selectedAsset.id, {
          ...basePrepare,
          tokenId: null,
          amount: amt,
          tokenStandard: 'ERC20'
        });
      } else if (isErc1155) {
        preparedMintPayload = await prepareMintToken(selectedAsset.id, {
          ...basePrepare,
          amount: amt,
          tokenStandard: 'ERC1155'
        });
      } else {
        preparedMintPayload = await prepareMintToken(selectedAsset.id, basePrepare);
      }
      setPreparedMint(preparedMintPayload);
      toast.success('Unsigned mint transaction ready. Sign offline, then upload or paste the signed raw transaction.');
    } catch (error) {
      const msg =
        error.response?.data?.message || error.response?.data?.Message || error.message || 'Failed to prepare mint';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBroadcastMint = async () => {
    if (!selectedClass?.contractAddress) {
      toast.error('Select a deployed contract.');
      return;
    }
    if (!preparedMint) {
      toast.error('Prepare an unsigned mint first.');
      return;
    }

    const bulkFlow = bulkMintLineItems.length > 0;
    const assetIdForMint = bulkFlow ? bulkMintLineItems[bulkMintCursor]?.assetId : selectedAsset?.id;
    if (!assetIdForMint) {
      toast.error(bulkFlow ? 'Bulk mint state is out of sync. Prepare again.' : 'Select an asset.');
      return;
    }
    if (!bulkFlow && !selectedAsset) {
      toast.error('Select an asset.');
      return;
    }

    let parsedSigned;
    try {
      parsedSigned = parseSignedTransactionInput(signedMintInput);
    } catch (e) {
      toast.error(e.message || 'Invalid signed transaction input');
      return;
    }
    const signedEntries = Array.isArray(parsedSigned) ? parsedSigned : [parsedSigned];
    let signedForStep;
    if (bulkFlow) {
      signedForStep = signedEntries.length === 1 ? signedEntries[0] : signedEntries[bulkMintCursor];
    } else if (signedEntries.length > 1) {
      toast.error('Paste one signed mint JSON for a single asset, or use bulk registration for a multi-result signer file.');
      return;
    } else {
      signedForStep = signedEntries[0];
    }
    if (!signedForStep?.signedTransactionHex) {
      toast.error(
        bulkFlow && signedEntries.length > 1
          ? `No signedTransactionHex for mint ${bulkMintCursor + 1} (signed file has ${signedEntries.length} result(s)).`
          : 'Signed transaction JSON must contain signedTransactionHex.'
      );
      return;
    }

    if (contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
      try {
        resolveContractArtifact(CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID, {
          abiJson: customContractAbiJson,
          bytecode: '',
          mintFunctionName: customMintFunctionName
        });
      } catch (e) {
        toast.error(e.message || 'Invalid custom contract ABI');
        return;
      }
      if (!String(customMintFunctionName || '').trim()) {
        toast.error('Mint function name is required.');
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const artifact = resolveContractArtifact(contractTemplateId, {
        abiJson: customContractAbiJson,
        bytecode: '',
        mintFunctionName: customMintFunctionName
      });
      const resolvedId = preparedMint?.resolvedTokenId ?? preparedMint?.ResolvedTokenId;
      const amt = String(mintAmount || '').trim();
      let mintRequest;
      if (isErc20) {
        if (!amt) {
          toast.error('Enter a mint amount.');
          return;
        }
        mintRequest = await submitMintToken(assetIdForMint, {
          signedTransactionHex: signedForStep.signedTransactionHex,
          tokenId: '0',
          amount: amt,
          ownerWallet: ownerWallet.trim(),
          contractAddress: selectedClass.contractAddress,
          contractAbi: artifact.abiJson,
          functionName: artifact.mintFunctionName,
          tokenStandard: 'ERC20'
        });
      } else if (isErc1155) {
        if (!resolvedId) {
          toast.error('Missing resolved token type id from prepared payload. Prepare the mint again.');
          return;
        }
        if (!amt) {
          toast.error('Enter a mint amount.');
          return;
        }
        mintRequest = await submitMintToken(assetIdForMint, {
          signedTransactionHex: signedForStep.signedTransactionHex,
          tokenId: String(resolvedId),
          amount: amt,
          ownerWallet: ownerWallet.trim(),
          contractAddress: selectedClass.contractAddress,
          contractAbi: artifact.abiJson,
          functionName: artifact.mintFunctionName,
          tokenStandard: 'ERC1155'
        });
      } else {
        if (!resolvedId) {
          toast.error('Missing resolved token id from prepared payload. Prepare the mint again.');
          return;
        }
        mintRequest = await submitMintToken(assetIdForMint, {
          signedTransactionHex: signedForStep.signedTransactionHex,
          tokenId: String(resolvedId),
          ownerWallet: ownerWallet.trim(),
          contractAddress: selectedClass.contractAddress,
          contractAbi: artifact.abiJson,
          functionName: artifact.mintFunctionName
        });
      }

      const mintedToken = await pollForMintCompletion(assetIdForMint);
      if (!mintedToken) {
        throw new Error('Mint was submitted but confirmation has not arrived yet.');
      }

      const statusPayload = {
        requestStatus: mintRequest.status || 'submitted',
        tokenId: mintedToken.tokenId,
        transactionHash: mintedToken.mintTransactionHash,
        ownerWallet: mintedToken.currentOwnerWallet,
        status: 'minted'
      };

      if (bulkFlow && bulkMintCursor < bulkMintLineItems.length - 1) {
        const next = bulkMintCursor + 1;
        setBulkMintCursor(next);
        setPreparedMint(bulkMintLineItems[next].prepared);
        if (signedEntries.length === 1) {
          setSignedMintInput('');
        }
        setMintStatus(statusPayload);
        toast.success(
          `Mint ${bulkMintCursor + 1} of ${bulkMintLineItems.length} broadcast. Sign the next unsigned JSON (${next + 1}/${bulkMintLineItems.length}).`
        );
        try {
          await loadAssets();
        } catch {
          /* ignore */
        }
        return;
      }

      if (bulkFlow) {
        setBulkMintLineItems([]);
        setBulkMintCursor(0);
        try {
          await loadAssets();
        } catch {
          /* ignore */
        }
      }

      setMintStatus(statusPayload);
      setPreparedMint(null);
      setSignedMintInput('');
      const okMsg = isErc20
        ? `Mint broadcast. Amount ${mintedToken.amount || amt}.`
        : bulkFlow
          ? `Batch complete. Last token ${mintedToken.tokenId || resolvedId || '—'}.`
          : `Mint broadcast. Token ${mintedToken.tokenId || resolvedId || '—'}.`;
      toast.success(okMsg);
    } catch (error) {
      const msg =
        error.response?.data?.message || error.response?.data?.Message || error.message || 'Failed to broadcast mint';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetMintFlow = () => {
    setPreparedMint(null);
    setSignedMintInput('');
    setMintStatus(null);
    setBulkMintLineItems([]);
    setBulkMintCursor(0);
  };

  const copyContractAddr = async (addr) => {
    await copyTextToClipboard(addr);
    toast.success('Contract address copied');
  };

  if (!hasSession) {
    return (
      <Page>
        <GatePanel>
          <FiLock />
          <h2>Sign in required</h2>
          <p>
            Offline mint is available after you sign in. Use <strong>Login</strong> in the header, then return here.
          </p>
        </GatePanel>
      </Page>
    );
  }

  if (!isAuthorizedTokenizer && !isAdmin) {
    return (
      <Page>
        <BackRow>
          <Link to="/tokenization">
            <FiArrowLeft aria-hidden />
            Back to Core Tokenizer
          </Link>
        </BackRow>
        <GatePanel>
          <FiLock />
          <h2>Not available for this account</h2>
          <p>Only authorized tokenizers and administrators can prepare and broadcast offline mint transactions.</p>
        </GatePanel>
      </Page>
    );
  }

  return (
    <Page>
      <TransactionLookupDrawer
        transactionHash={inspectedTransactionHash}
        onClose={() => setInspectedTransactionHash(null)}
      />
      <BackRow>
        <Link to="/tokenization">
          <FiArrowLeft aria-hidden />
          Back to Core Tokenizer
        </Link>
      </BackRow>

      <TitleBlock>
        <h1>
          <FiLayers aria-hidden />
          Offline mint — deployed contracts
        </h1>
        <p>
          See asset classes that already have an on-chain contract. Mint <strong>one asset</strong> or, for ERC-721, an
          entire <strong>bulk registration</strong> in one prepare (API{' '}
          <code style={{ color: '#94a3b8' }}>POST /api/mint-requests/bulk-prepare-by-registration</code>), then sign and
          broadcast each unsigned transaction in order.
        </p>
      </TitleBlock>

      <Section>
        <h2>Deployed contracts you can mint from</h2>
        <p style={{ color: '#a0a0a0', marginBottom: '1rem', fontSize: '0.9rem' }}>
          {isAdmin
            ? 'All deployed classes are listed. Tokenizers only see classes tied to their assets.'
            : 'Only classes where you have at least one asset are shown.'}{' '}
          <strong style={{ color: '#cbd5e1' }}>Click a card</strong> to open mint options — including{' '}
          <strong style={{ color: '#cbd5e1' }}>Bulk registration (ERC-721)</strong> for catalog CBC721 / ERC-721 classes.
        </p>
        {deployedForUser.length === 0 ? (
          <p style={{ color: '#a0a0a0' }}>
            No deployed contracts yet. Deploy from <Link to="/asset-classes">Asset classes</Link> or use{' '}
            <Link to="/tokenization" style={{ color: '#9bd6ff' }}>
              Tokenize Asset
            </Link>{' '}
            in Core Tokenizer.
          </p>
        ) : (
          <ContractGrid>
            {deployedForUser.map((ac) => {
              const mintableCount = assets.filter(
                (a) => a.assetClassId === ac.id && isMintReadyAsset(a)
              ).length;
              return (
                <ContractCard
                  key={ac.id}
                  role="button"
                  tabIndex={0}
                  $active={selectedClassId === ac.id}
                  onClick={() => setSelectedClassId(ac.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedClassId(ac.id);
                    }
                  }}
                >
                  <div className="name">{ac.name}</div>
                  <div className="code">{ac.code || ac.id}</div>
                  <div className="addr">{ac.contractAddress}</div>
                  <MiniButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyContractAddr(ac.contractAddress);
                    }}
                  >
                    <FiCopy />
                    Copy address
                  </MiniButton>
                  <div className="meta">
                    Standard: {ac.tokenStandard || '—'} · Mint-ready assets: {mintableCount}
                    {normalizeAssetClassTokenStandard(ac.tokenStandard) === 'ERC721' && (
                      <span style={{ display: 'block', marginTop: '0.35rem', color: '#7dd3fc', fontWeight: 600 }}>
                        Bulk batch mint available after you select this class
                      </span>
                    )}
                  </div>
                </ContractCard>
              );
            })}
          </ContractGrid>
        )}
        {deployedForUser.length > 1 && !selectedClassId && (
          <p
            style={{
              marginTop: '1rem',
              padding: '0.85rem 1rem',
              borderRadius: '10px',
              border: '1px solid rgba(125, 211, 252, 0.35)',
              background: 'rgba(56, 189, 248, 0.08)',
              color: '#bae6fd',
              fontSize: '0.9rem',
              lineHeight: 1.5
            }}
          >
            Select one of the contract cards above. The next section (mint mode, asset or bulk registration, prepare) appears
            only after a class is selected.
          </p>
        )}
      </Section>

      {selectedClass && (
        <Section>
          <h2>Mint for: {selectedClass.name}</h2>
          {!isClassErc721 && (
            <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginTop: '-0.35rem', marginBottom: '1rem', lineHeight: 1.55 }}>
              <strong>Bulk registration (ERC-721)</strong> is hidden here because this class is not ERC-721 (e.g. CBC1155 /
              ERC-1155 or CBC20). Use <strong>Single asset</strong> mint below when you have a mint-ready asset.
            </p>
          )}

          <FormPanel>
            {isClassErc721 && (
              <Field>
                <span style={{ display: 'block', color: '#a0a0a0', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Mint mode
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="offline-mint-mode"
                      checked={mintMode === 'single'}
                      onChange={() => setMintMode('single')}
                    />
                    Single asset
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="offline-mint-mode"
                      checked={mintMode === 'bulk'}
                      onChange={() => setMintMode('bulk')}
                    />
                    Bulk registration (ERC-721)
                  </label>
                </div>
                {mintMode === 'bulk' && (
                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: '0.5rem', marginBottom: 0 }}>
                    Prepare once to receive one unsigned JSON per member (sequential nonces). Sign and broadcast in order
                    without skipping.
                  </p>
                )}
              </Field>
            )}

            {mintMode === 'single' ? (
              <Field>
                <label htmlFor="offline-mint-asset">Asset</label>
                <select
                  id="offline-mint-asset"
                  value={selectedAssetId}
                  onChange={(e) => setSelectedAssetId(e.target.value)}
                >
                  <option value="">Select a mint-ready asset</option>
                  {mintableForClass.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.status}) — #{a.assetNumber || a.id}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field>
                <label htmlFor="offline-mint-bulk">Bulk registration (this class)</label>
                <select
                  id="offline-mint-bulk"
                  value={selectedBulkId}
                  onChange={(e) => setSelectedBulkId(e.target.value)}
                  disabled={bulkRegsLoading || !isClassErc721}
                >
                  <option value="">
                    {bulkRegsLoading ? 'Loading bulk registrations…' : 'Select a bulk batch (all members mint-ready)'}
                  </option>
                  {eligibleBulkRegs.map((b) => {
                    const n = bulkRegistrationMembers(b).length;
                    const title = b.title ?? b.Title ?? 'Bulk registration';
                    const bid = b.id ?? b.Id;
                    return (
                      <option key={bid} value={bid}>
                        {title} — {n} member{n === 1 ? '' : 's'}
                      </option>
                    );
                  })}
                </select>
                {!bulkRegsLoading && isClassErc721 && eligibleBulkRegs.length === 0 && bulkRegsForClass.length > 0 && (
                  <p style={{ color: '#fbbf24', fontSize: '0.82rem', marginTop: '0.45rem', marginBottom: 0 }}>
                    This class has bulk batches, but none have every member approved and ready to mint yet.
                  </p>
                )}
                {!bulkRegsLoading && isClassErc721 && bulkRegsForClass.length === 0 && bulkRegistrations.length > 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: '0.45rem', marginBottom: 0 }}>
                    No bulk registrations for this asset class. Create one via <strong>Create many similar assets</strong>.
                  </p>
                )}
              </Field>
            )}
            <Field>
              <label htmlFor="offline-mint-template">Standard contract template (ABI for mint)</label>
              <select
                id="offline-mint-template"
                value={contractTemplateId}
                onChange={(e) => setContractTemplateId(e.target.value)}
              >
                {listStandardContractTemplates().map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.versionLabel})
                  </option>
                ))}
              </select>
              <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: '0.35rem', marginBottom: 0 }}>
                Must match the contract deployed for this class. Defaults from the class token standard (CBC721 /
                CBC20 / CBC1155); use Custom for your own Ping ABI and mint function.
              </p>
            </Field>
            {contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && (
              <>
                <Field>
                  <label htmlFor="offline-mint-custom-abi">Contract ABI (JSON array) *</label>
                  <textarea
                    id="offline-mint-custom-abi"
                    placeholder='[{"type":"function","name":"mint",...}]'
                    value={customContractAbiJson}
                    onChange={(e) => setCustomContractAbiJson(e.target.value)}
                  />
                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: '0.35rem', marginBottom: 0 }}>
                    Paste the ABI of the contract at the class address (no bytecode needed for offline mint).
                  </p>
                </Field>
                <Field>
                  <label htmlFor="offline-mint-custom-mint-fn">Mint function name *</label>
                  <input
                    id="offline-mint-custom-mint-fn"
                    value={customMintFunctionName}
                    onChange={(e) => setCustomMintFunctionName(e.target.value)}
                    placeholder="mint"
                    autoComplete="off"
                  />
                </Field>
              </>
            )}
            {mintableForClass.length === 0 && (
              <p style={{ color: '#a0a0a0', fontSize: '0.9rem' }}>
                No assets in this class are ready to mint (approved / metadata on IPFS, not yet tokenized).
              </p>
            )}

            <Field>
              <label htmlFor="offline-mint-signer">Offline signer address</label>
              <input
                id="offline-mint-signer"
                value={signerAddress}
                onChange={(e) => setSignerAddress(e.target.value)}
                placeholder="0x…"
                autoComplete="off"
              />
              <p style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '0.35rem', marginBottom: 0 }}>
                Remembered for your account in this browser.
              </p>
            </Field>
            <Field>
              <label htmlFor="offline-mint-owner">Recipient wallet (token owner)</label>
              <input
                id="offline-mint-owner"
                value={ownerWallet}
                onChange={(e) => setOwnerWallet(e.target.value)}
                placeholder="0x…"
                autoComplete="off"
              />
            </Field>
            {(isErc20 || isErc1155) && (
              <Field>
                <label htmlFor="offline-mint-amount">
                  {isErc20 ? 'Mint amount (smallest units) *' : 'ERC-1155 mint amount *'}
                </label>
                <input
                  id="offline-mint-amount"
                  value={mintAmount}
                  onChange={(e) => setMintAmount(e.target.value)}
                  placeholder={isErc20 ? '1000000000000000000' : '1'}
                  autoComplete="off"
                />
                <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: '0.35rem', marginBottom: 0 }}>
                  {isErc20
                    ? 'Integer string — catalog CBC20 uses 18 decimals unless your contract differs.'
                    : 'Units minted for the allocated token type id.'}
                </p>
              </Field>
            )}
            <Field>
              <label>Resolved mint parameters</label>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
                {isErc20 && (
                  <>
                    ERC-20 mint uses your amount above; the prepared payload encodes <code>mint(to, amount)</code>.
                  </>
                )}
                {isErc1155 && (
                  <>
                    The app allocates the next ERC-1155 token type id when you prepare. It appears as{' '}
                    <code>resolvedTokenId</code> in the JSON together with your amount.
                  </>
                )}
                {!isErc20 && !isErc1155 && (
                  <>
                    The app picks the next free ERC-721 id on your class contract when you prepare. After prepare, the
                    assigned id appears in the JSON as <code>resolvedTokenId</code>.
                  </>
                )}
              </p>
              {(preparedMint?.resolvedTokenId ?? preparedMint?.ResolvedTokenId) && (
                <p style={{ color: '#e2e8f0', marginTop: '0.5rem', marginBottom: 0 }}>
                  Current prepared id:{' '}
                  <strong>{preparedMint.resolvedTokenId ?? preparedMint.ResolvedTokenId}</strong>
                </p>
              )}
            </Field>
          </FormPanel>

          <StatusPanel>
            <div className="title">Offline signing &amp; broadcast</div>
            <FlowSteps>
              <li>
                <strong>Prepare unsigned mint</strong> — Creates the JSON your offline signer needs. Keys never leave the
                signer.
              </li>
              <li>
                <strong>Sign</strong> — Copy or download the JSON, sign with the address you entered.
              </li>
              <li>
                <strong>Upload or paste</strong> — Load the signer output or raw <code>0x</code> signed transaction; submit
                to broadcast via the API.
              </li>
            </FlowSteps>
            <div className="meta">
              <span>Contract: {selectedClass.contractAddress}</span>
              <span>Mint function: {resolvedMintArtifactPreview?.mintFunctionName ?? '—'}</span>
            </div>
          </StatusPanel>

          {bulkMintLineItems.length > 0 && preparedMint && (
            <StatusPanel>
              <div className="title">Bulk batch progress</div>
              <div className="message">
                Prepare step <strong>{bulkMintCursor + 1}</strong> of <strong>{bulkMintLineItems.length}</strong> — sign and
                broadcast this JSON before moving to the next.
              </div>
            </StatusPanel>
          )}

          {mintStatus?.status === 'minted' && (
            <StatusPanel>
              <div className="title">Last mint</div>
              <div className="message">Mint transaction was broadcast and reconciled.</div>
              <div className="meta">
                <span>
                  Token: <strong style={{ color: '#fff' }}>{mintStatus.tokenId}</strong>
                </span>
                <span>
                  Tx:{' '}
                  {mintStatus.transactionHash ? (
                    <HashButton type="button" onClick={() => setInspectedTransactionHash(mintStatus.transactionHash)}>
                      {mintStatus.transactionHash}
                    </HashButton>
                  ) : (
                    '—'
                  )}
                </span>
                <span>
                  Request:{' '}
                  <StatusBadge>{mintStatus.requestStatus || 'submitted'}</StatusBadge>
                </span>
              </div>
            </StatusPanel>
          )}

          {preparedMint && (
            <>
              <PayloadBox>
                <div className="title">Unsigned mint transaction</div>
                <div className="hint">
                  Sign this payload offline. Do not paste private keys into the browser.
                </div>
                <PayloadActions>
                  <MiniButton type="button" onClick={() => handleCopyPayload(preparedMint)}>
                    <FiCopy />
                    Copy JSON
                  </MiniButton>
                  <MiniButton
                    type="button"
                    onClick={() => {
                      const labelAsset =
                        bulkMintLineItems.length > 0
                          ? assets.find((x) => x.id === bulkMintLineItems[bulkMintCursor]?.assetId)
                          : selectedAsset;
                      handleDownloadPayload(
                        `${labelAsset?.assetNumber || labelAsset?.id || 'asset'}-mint-prepared.json`,
                        preparedMint
                      );
                    }}
                  >
                    <FiDownload />
                    Download JSON
                  </MiniButton>
                </PayloadActions>
                <textarea readOnly value={formatPreparedPayload(preparedMint)} />
              </PayloadBox>
              <PayloadBox>
                <div className="title">Signer output template</div>
                <div className="hint">Your signer should produce JSON containing signedTransactionHex.</div>
                <PayloadActions>
                  <MiniButton type="button" onClick={handleCopyTemplate}>
                    <FiCopy />
                    Copy template
                  </MiniButton>
                </PayloadActions>
                <textarea readOnly value={signerOutputTemplate} />
              </PayloadBox>
              <PayloadBox>
                <div className="title">Signed mint — upload or paste</div>
                <div className="hint">
                  Upload a JSON file from your signer or paste JSON / raw <code>signedTransactionHex</code>.
                </div>
                <PayloadActions>
                  <FilePickLabel>
                    <FiUpload aria-hidden />
                    Upload signed file
                    <input
                      type="file"
                      accept=".json,.txt,text/json,application/json"
                      onChange={handleSignedFileSelected}
                    />
                  </FilePickLabel>
                </PayloadActions>
                <textarea
                  value={signedMintInput}
                  onChange={(e) => setSignedMintInput(e.target.value)}
                  placeholder="Paste signedTransactionHex or full signer JSON here…"
                />
              </PayloadBox>
            </>
          )}

          <ActionRow>
            <SecondaryButton type="button" onClick={() => loadAssetClasses()} disabled={loading}>
              <FiRefreshCw />
              Refresh classes
            </SecondaryButton>
            {mintMode === 'bulk' && (
              <SecondaryButton type="button" onClick={refreshBulkRegistrations} disabled={bulkRegsLoading}>
                <FiRefreshCw />
                Refresh bulk list
              </SecondaryButton>
            )}
            <SecondaryButton
              type="button"
              onClick={resetMintFlow}
              disabled={!preparedMint && !signedMintInput && bulkMintLineItems.length === 0}
            >
              Clear prepared / signed
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={handlePrepareMint}
              disabled={
                loading ||
                isSubmitting ||
                Boolean(preparedMint) ||
                (mintMode === 'single' && (!selectedAssetId || isErc1155)) ||
                (mintMode === 'bulk' && (!selectedBulkId || !isClassErc721 || bulkRegsLoading || isErc1155))
              }
            >
              <FiCpu />
              Prepare unsigned mint
            </PrimaryButton>
            <PrimaryButton
              type="button"
              onClick={handleBroadcastMint}
              disabled={
                loading ||
                isSubmitting ||
                !preparedMint ||
                !signedMintInput.trim() ||
                (mintMode === 'single' && isErc1155)
              }
            >
              <FiCpu />
              Broadcast signed mint
            </PrimaryButton>
          </ActionRow>
        </Section>
      )}
    </Page>
  );
}

export default OfflineTokenMintPage;
